import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewContainerRef,
  input,
  viewChild,
} from '@angular/core';
import { isEqual } from 'lodash-es';
import { ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import SwiperCore, { Autoplay, Navigation, Pagination, SwiperOptions } from 'swiper';

import {
  LARGE_BREAKPOINT_WIDTH,
  MEDIUM_BREAKPOINT_WIDTH,
  SMALL_BREAKPOINT_WIDTH,
} from 'ish-core/configurations/injection-keys';
import { ProductContextDisplayProperties } from 'ish-core/facades/product-context.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { InjectSingle } from 'ish-core/utils/injection';
import { ProductItemDisplayType } from 'ish-shared/components/product/product-item/product-item.component';

import { IcmCarousalContentlet } from '../../../models/dot-cms-uve-contentlet.model';
import { configureSlides, configureSwiper } from '../../../utils/dot-cms-uve-utils';

import { DotCmsUveProductSwiperComponent } from './dot-cms-uve-product-swiper/dot-cms-uve-product-swiper.component';

export interface DotCmsDynamicProducts {
  swiperConfig?: SwiperOptions;
  contentlet?: IcmCarousalContentlet;
  products?: string[];
  listItemStyle?: ProductItemDisplayType;
  listStyle?: string;
  displayProperties?: Partial<ProductContextDisplayProperties>;
}

SwiperCore.use([Autoplay, Pagination, Navigation]);

@Component({
  selector: 'custom-dot-cms-uve-product-carousel-list',
  templateUrl: './dot-cms-uve-product-carousel-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class DotCmsUveProductCarouselListComponent implements OnInit, OnChanges, OnDestroy {
  readonly listStyle = input<string>(undefined);
  readonly listItemStyle = input<ProductItemDisplayType>(undefined);
  readonly contentlet = input<IcmCarousalContentlet>(undefined);
  productSKUs$ = new ReplaySubject<string[]>(1);
  productSKUs: string[];
  breakPointWidths: number[] = [];
  displayProperties: Partial<ProductContextDisplayProperties>;
  swiperConfig: SwiperOptions;
  dotCmsDynamicProducts$ = new ReplaySubject<DotCmsDynamicProducts>(1);
  configuredSwiper: SwiperOptions;
  private destroy$ = new Subject<void>();

  readonly productCarousel = viewChild('productCarousel', { read: ViewContainerRef });

  constructor(
    @Inject(SMALL_BREAKPOINT_WIDTH) private smallBreakpointWidth: InjectSingle<typeof SMALL_BREAKPOINT_WIDTH>,
    @Inject(MEDIUM_BREAKPOINT_WIDTH) private mediumBreakpointWidth: InjectSingle<typeof MEDIUM_BREAKPOINT_WIDTH>,
    @Inject(LARGE_BREAKPOINT_WIDTH) private largeBreakpointWidth: InjectSingle<typeof LARGE_BREAKPOINT_WIDTH>,
    private shoppingFacade: ShoppingFacade
  ) {
    this.swiperConfig = {
      direction: 'horizontal',
      navigation: true,
      observer: true,
      observeParents: true,
    };

    this.breakPointWidths = [this.smallBreakpointWidth, this.mediumBreakpointWidth, this.largeBreakpointWidth];
  }
  ngOnChanges(): void {
    const contentlet = this.contentlet();
    if (contentlet.productSkus) {
      this.productSKUsSplit(contentlet);
      this.configureDisplayProperties(contentlet);

      // remove all SKUs from the productSKUs Array that are also contained in the failed products Array
      combineLatest([of(this.productSKUs), this.shoppingFacade.failedProducts$])
        .pipe(
          distinctUntilChanged<[string[], string[]]>(isEqual),
          map(([skus, failed]) => skus.filter(x => !failed.includes(x))),
          takeUntil(this.destroy$)
        )
        .subscribe(products => {
          this.productSKUs$.next(products);
        });
    }
  }

  ngOnInit(): void {
    if (this.productSKUs$) {
      this.productSKUs$.pipe(takeUntil(this.destroy$)).subscribe(products => {
        const contentlet = this.contentlet();
        if (
          contentlet.slideItems !== 0 &&
          contentlet.productSkus.split(',').length &&
          contentlet.productSkus.split(',').length > contentlet.slideItems &&
          contentlet?.noOfItems > contentlet.slideItems
        ) {
          Object.entries(configureSwiper(contentlet, this.breakPointWidths)).map(([key, value]) => {
            //@ts-ignore
            this.swiperConfig[key] = value;
          });

          this.dotCmsDynamicProducts$.next({
            swiperConfig: this.swiperConfig,
            contentlet,
            products,
            listItemStyle: this.listItemStyle(),
            listStyle: this.listStyle(),
            displayProperties: this.displayProperties,
          });
        } else {
          this.swiperConfig.breakpoints = configureSlides(
            products.length < contentlet.noOfItems ? products.length : contentlet.noOfItems,
            this.breakPointWidths
          );
          this.dotCmsDynamicProducts$.next({
            swiperConfig: this.swiperConfig,
            contentlet,
            products,
            listItemStyle: this.listItemStyle(),
            listStyle: this.listStyle(),
            displayProperties: this.displayProperties,
          });
        }
      });

      this.dotCmsDynamicProducts$.pipe(takeUntil(this.destroy$)).subscribe(swiperProducts => {
        this.createDynamicSwiper(this.rearrangeProducts(swiperProducts));
      });
    }
  }
  productSKUsSplit(contentlet: IcmCarousalContentlet) {
    const skuArray = contentlet?.productSkus.split(',');
    contentlet?.noOfItems <= skuArray.length - 1
      ? contentlet?.noOfItems === 0
        ? (this.productSKUs = skuArray.map(data => data.trim()))
        : (this.productSKUs = skuArray.map(data => data.trim()).slice(0, contentlet.noOfItems))
      : (this.productSKUs = skuArray.map(data => data.trim()));
  }

  configureDisplayProperties(contentlet: IcmCarousalContentlet) {
    if (contentlet && contentlet.show) {
      // const componentToBeShown = contentlet?.show?.trim().split(',');
      this.displayProperties = {
        image: contentlet?.show.includes('image') || false,
        price: contentlet?.show.includes('price') || false,
        name: contentlet?.show.includes('title') || false,
        addToBasket: contentlet?.show.includes('addToCart') || false,
      };
    }
  }

  rearrangeProducts(dynamicProducts: DotCmsDynamicProducts) {
    if (
      dynamicProducts.contentlet.loop &&
      dynamicProducts.contentlet.slideItems !== 0 &&
      dynamicProducts.products.length > this.contentlet().slideItems &&
      dynamicProducts.products.length % this.contentlet().slideItems !== 0
    ) {
      let productIn = dynamicProducts.products;
      const sliceval = this.contentlet().slideItems - (dynamicProducts.products.length % this.contentlet().slideItems);
      productIn = [...productIn, ...productIn.slice(0, sliceval)];
      dynamicProducts.products = productIn;
    }
    return dynamicProducts;
  }
  createDynamicSwiper(swiperProducts: DotCmsDynamicProducts) {
    const scrollY = !SSR ? window.scrollY || document.documentElement.scrollTop : 0;
    this.productCarousel().clear();
    const componentRef = this.productCarousel().createComponent(DotCmsUveProductSwiperComponent);
    componentRef.setInput('swiperProducts', swiperProducts);
    if (!SSR && scrollY !== 0) {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollY,
          behavior: 'instant' as ScrollBehavior,
        });
      });
    }

    componentRef.changeDetectorRef.markForCheck();
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
